import { Flex, Text, Heading, BoxProps, Box } from "@chakra-ui/react";
import { useQuery, skipToken } from "@tanstack/react-query";
import {
  ContentSectionCustomCard,
  Section,
  Release,
  Bundle,
} from "../../types";
import { ComicFrequencyStatusBadge } from "./components/ComicFrequencyStatusBadge";
import { ComicReadingStyleBadge } from "./components/ComicReadingStyleBadge";
import { PDFBadge } from "./components/PDFBadge";
import { CONTEXT_ENTITY_TYPES } from "./constants";
import { useMobileBreakpoint } from "../../utils/useBreakpoints";
import { useNavigateToReaderWithContext } from "../../screens/reader/hooks/useNavigateToReaderWithContext";
import { FeatureCardALCButtons } from "./FeatureCardALCButtons";
import { NavigationTrigger } from "../../screens/reader/types";
import { PriceDisplay } from "./components/PriceDisplay";
import { useBundlePricing } from "../../hooks/useBundlePricing";

interface FeatureCardContentProps extends BoxProps {
  contentCard: ContentSectionCustomCard;
  isHidden?: boolean;
  cardResponsiveWidths?: Record<string, string> | string;
  isRoadblock?: boolean;
  index?: number;
  sectionIndex?: number;
  section?: Section;
  isLocked?: boolean;
}

export function FeatureCardContent({
  contentCard,
  isHidden = false,
  cardResponsiveWidths,
  isRoadblock = false,
  index,
  sectionIndex,
  section,
  ...props
}: FeatureCardContentProps) {
  const isMobile = useMobileBreakpoint();
  const isComicCard =
    contentCard.context_entity_type_id === CONTEXT_ENTITY_TYPES.comic;

  // Cache the release data so it can be updated after purchase
  const releaseCacheKey =
    !isComicCard && contentCard.release
      ? `${contentCard.release.entity_type}-${contentCard.release.id}`
      : null;

  const { data: cachedRelease } = useQuery<Release>({
    queryKey: [releaseCacheKey],
    queryFn: skipToken,
    enabled: !!releaseCacheKey,
    networkMode: "offlineFirst",
  });

  // Cache bundle data similar to release caching
  const bundleCacheKey = contentCard.bundle
    ? `${contentCard.bundle.entity_type}-${contentCard.bundle.id}`
    : null;

  const { data: cachedBundle } = useQuery<Bundle>({
    queryKey: [bundleCacheKey],
    queryFn: skipToken,
    enabled: !!bundleCacheKey,
    networkMode: "offlineFirst",
  });

  const cardDataToUse = {
    ...contentCard,
    release: cachedRelease ?? contentCard.release,
    bundle: cachedBundle ?? contentCard.bundle,
  };
  const comicDataToUse = isComicCard
    ? cardDataToUse.comic
    : cardDataToUse.release?.comic;
  const bundleDataToUse = cardDataToUse.bundle;

  // Bundle pricing logic - always call hook, it handles null bundles
  const { priceToUse: bundlePrice, hasDiscount } = useBundlePricing(bundleDataToUse);

  const { goToCTAUrl, goToCTAUrlWithContext, navigationContext } =
    useNavigateToReaderWithContext({
    comicToNavigate:
      isComicCard && comicDataToUse?.read_url ? comicDataToUse : undefined,
    releaseToNavigate:
      !isComicCard && cardDataToUse.release?.read_url
        ? cardDataToUse.release
        : undefined,
    section: section,
    cardPosition: index,
    sectionPosition: sectionIndex,
    trigger: NavigationTrigger.featureCardClicked,
  });

  // Get release data for PDF badge logic
  const releaseData =
    !isComicCard && cardDataToUse.release ? cardDataToUse.release : undefined;

  return (
    <Flex
      direction="column"
      mt={isRoadblock && !isMobile ? undefined : "16px"}
      width={cardResponsiveWidths}
      height={
        isRoadblock
          ? "250x"
          : {
              base: "242px",
            }
      }
      justifyContent="space-between"
      alignItems="start"
      position="relative"
      opacity={isHidden ? 0.2 : 1}
      transition="all .5s ease"
      {...props}
    >
      <Flex direction="column" flex="1" width="100%">
        {comicDataToUse ? (
          <Flex
            alignItems="center"
            width="100%"
            justifyContent={
              isRoadblock && !isMobile ? "start" : "space-between"
            }
          >
            {isComicCard ? (
              <ComicFrequencyStatusBadge
                comic={comicDataToUse}
                fontSize="10px"
              />
            ) : (
              <Text fontSize="12px" fontWeight={700} color="neutral.300">
                {comicDataToUse?.name}
              </Text>
            )}
            <Flex gap="8px" ml={isRoadblock && !isMobile ? "16px" : "auto"}>
              {releaseData?.pdf_download_sale_available && (
                <PDFBadge fontSize="10px" />
              )}
              <ComicReadingStyleBadge
                fontSize="10px"
                comic={comicDataToUse}
                release={releaseData}
              />
            </Flex>
          </Flex>
        ) : bundleDataToUse ? (
          <Flex
            alignItems="center"
            width="100%"
            justifyContent={
              isRoadblock && !isMobile ? "start" : "space-between"
            }
          >
            <Text fontSize="12px" fontWeight={700} color="neutral.300">
              Bundle • {bundleDataToUse.total_series} series
            </Text>
          </Flex>
        ) : null}
        <Heading
          as="h2"
          fontSize={{ base: "15px", sm: "20px" }}
          fontWeight={700}
          lineHeight="130%"
          color="neutral.white"
          w="100%"
          mt="8px"
          noOfLines={1}
          className="multiline-clamp"
        >
          {cardDataToUse.display_name}
        </Heading>
        <Text
          fontSize="12px"
          fontWeight={700}
          color="neutral.300"
          textAlign="left"
          w="100%"
          mt="8px"
        >
          {bundleDataToUse
            ? `${bundleDataToUse.owner.name} • ${bundleDataToUse.total_items} items`
            : `${comicDataToUse?.artist?.roman_name} • ${comicDataToUse?.category_name}`}
        </Text>
        <Text
          fontSize={isRoadblock ? "14px" : "12px"}
          fontWeight={400}
          color="neutral.300"
          lineHeight="150%"
          textAlign="left"
          w="full"
          maxH={isRoadblock ? undefined : "75px"}
          mt="8px"
          noOfLines={5}
          className="multiline-clamp"
        >
          {cardDataToUse.description
            ? cardDataToUse.description
            : isComicCard
              ? comicDataToUse?.description
              : cardDataToUse?.release?.description}
        </Text>
        {bundleDataToUse && bundlePrice && (
          <Box mt="8px" alignSelf="flex-start">
            <PriceDisplay
              price={bundlePrice}
              originalPrice={hasDiscount ? bundleDataToUse.price : undefined}
              hasDiscount={hasDiscount}
            />
          </Box>
        )}
      </Flex>
      {comicDataToUse || bundleDataToUse ? (
        <FeatureCardALCButtons
          contentCard={cardDataToUse}
          isHidden={isHidden}
          isRoadblock={isRoadblock}
          index={index}
          sectionIndex={sectionIndex}
          section={section}
          goToCTAUrl={goToCTAUrl}
          goToCTAUrlWithContext={goToCTAUrlWithContext}
          navigationContext={navigationContext}
          buttonWidth={
            !(isRoadblock && !isMobile)
              ? "full"
              : { base: "200px", sm: "278px", md: "350px" }
          }
        />
      ) : null}
    </Flex>
  );
}
